/** @jsx isolateComponent */

import { Fragment } from 'react';

import {
  ColorName,
  Colors,
  Icon,
  IconName,
  Tooltip,
  Typography,
} from '@zapier/design-system';
import {
  FeatureListing,
  Size,
  TooltipPosition,
} from '@zapier/design-system-revenue/src/types';
import { Hoverable, HoverableState } from '@zapier/common-components';
import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/common-utils';

type PlanFeatureProps = {
  /** A single plan feature with optional tooltip. */
  feature: Omit<FeatureListing, '__typename'>;
  /** The color of the icon to display. */
  iconColor?: ColorName;
  /** The name of the icon to display. */
  iconName?: IconName;
  /** True if this feature should have emphatic styling applied to it. */
  isEmphasized?: boolean;
  /** The size of the screen. */
  size: Size;
  /** The direction in which the tooltip should appear. */
  tooltipPosition?: TooltipPosition;
};

const Styles = labelStyles(__filename, {
  root: {
    cursor: 'default', // prevents text cursor
    display: 'flex',
    paddingBottom: '8px',
  },

  featureName: (
    size: Size,
    isHovered: boolean,
    isEmphasized: boolean,
    hasTooltip: boolean,
    hasHref: boolean
  ) => [
    {
      ...Typography.altText900,
      color: Colors.grey700,
    },

    withModifier('isEmphasized', {
      fontWeight: isEmphasized ? 'bold' : 'normal',
    }),

    // Show underline only on hover for large display, always on small and medium display.
    withModifier('hasTooltip', {
      borderBottom:
        hasTooltip && ((size === 'large' && isHovered) || size !== 'large')
          ? `1px dashed ${Colors.grey200}`
          : 'none',
    }),

    withModifier('hasHref', {
      cursor: hasHref ? 'pointer' : 'default',
    }),
  ],

  icon: {
    alignSelf: 'flex-start',
    marginRight: '10px',
  },
});

const displayTooltip = (tooltip: string) => {
  return tooltip.split('\n').map((line, i) => {
    return <p key={i}>{line}</p>;
  });
};

/**
 * A component that displays a single plan feature with optional tooltip.
 */
export const PlanFeature = ({
  feature,
  iconColor = 'green500',
  iconName = 'formCheck',
  isEmphasized = false,
  size,
  tooltipPosition = 'north',
}: PlanFeatureProps) => (
  <div css={Styles.root}>
    <div css={Styles.icon}>
      <Icon color={iconColor} name={iconName} size={20} />
    </div>

    <Hoverable>
      {({ isHovered }: HoverableState) => (
        <Fragment>
          <p
            css={Styles.featureName(
              size,
              isHovered,
              isEmphasized,
              !!feature.tooltip,
              !!feature.href
            )}
          >
            {feature.href ? (
              <a
                href={feature.href}
                style={{ color: Colors.grey700 }}
                target="_blank"
              >
                {feature.name}
              </a>
            ) : (
              feature.name
            )}
          </p>

          {feature.tooltip && isHovered && (
            <div
              aria-describedby={feature.name}
              style={{
                bottom: 25,
                position: 'relative',
              }}
            >
              <Tooltip
                allowMultiline={true}
                id={feature.name}
                position={tooltipPosition}
              >
                <div
                  style={{
                    width: 270,
                  }}
                >
                  {displayTooltip(feature.tooltip)}
                </div>
              </Tooltip>
            </div>
          )}
        </Fragment>
      )}
    </Hoverable>
  </div>
);
